import React, { useContext } from 'react';
import { useHistory } from 'react-router';
import {
  RecommendationContentTypeDto,
  RecommendationDto,
  RemediationActivityTypeDto,
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import RemediationQuiz from '../remediation-quiz/RemediationQuiz.component';
import {
  ActivityTypeConfigMap,
  RemediationContentTypeConfigMap
} from '../../models/remediation.models';
import RecommendationCard from '../recommendation-card/RecommendationCard.component';
import { AppContext } from '../app/AppProvider.component';
import { RoutePath } from '../app/app.constants';
import {
  getAssessmentGoalFromRecommendation,
  getCategoryGroupNameForGoal,
  getGoalBreadcrumbs,
  getQuizTotals,
  handleResumeOrStartNewAttempt
} from '../../utilities/remediation.utilities';
import { trackAdobeAction, trackNewRelicAction } from '../app/app-state-actions.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import {
  getSecondRecommendation
} from '../../utilities/recommendation-priority.utilities';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';

type RecommendationPostPlayerNextPropsOnly = {
  nextRecommendation: RecommendationDto;
}

export type RecommendationPostPlayerNextProps = RecommendationPostPlayerNextPropsOnly;

export const RecommendationPostPlayerNextComponent = (props: RecommendationPostPlayerNextProps): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;
  const history = useHistory();

  const {
    nextRecommendation,
  } = props;

  const setIsLaunching = (isLaunchingRecommendation: boolean) => {
    setAppState((prev) => ({ ...prev, isLaunchingRecommendation }));
  };

  if (!nextRecommendation) {
    return null;
  }

  // Helper function to create a banner for a recommendation
  const getBannerForRecommendation = (recommendation: RecommendationDto, isSecondRecommendation = false) => {
    const goal = getAssessmentGoalFromRecommendation(baseState?.eolsAssessment, recommendation);
    const contentTypeConfig = RemediationContentTypeConfigMap[recommendation.contentType];
    const activityTypeConfig = ActivityTypeConfigMap[contentTypeConfig.activityType];

    let banner = null;

    if (contentTypeConfig.activityType === RemediationActivityTypeDto.REVIEW_BY_TAXON) {
      banner = (
        <div>
          <strong>{activityTypeConfig.title}</strong> {isSecondRecommendation ? 'another focus topic' : 'your next focus topic'}, <strong>{
          getGoalBreadcrumbs(goal, getCategoryGroupNameForGoal(goal, baseState)).pathFullDisplay
        }:</strong>
        </div>
      );
    }

    if (contentTypeConfig.activityType === RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE) {
      banner = (
        <div>
          <strong>{activityTypeConfig.title}</strong> your knowledge with a related <strong>{contentTypeConfig.displayName}</strong>
        </div>
      );
    }

    return banner;
  };

  // Helper function to create a launch handler for a recommendation
  const createLaunchHandler = (recommendation: RecommendationDto) => {
    return () => {
      setIsLaunching(true);
      trackAdobeAction({
        appStateRef,
        actionProps: {
          action: AdobeAnalyticsAction.BUTTON_CLICK,
          pageData: {
            buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:launchNextRecommendation:${recommendation.contentType}`,
          }
        }
      });
      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.NEXT_RECOMMENDATION_LAUNCHED,
          props: {
            recommendationId: recommendation.id,
            contentType: recommendation.contentType,
            remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
            assessmentId: baseState.eolsAssessment?.id
          }
        }
      });
      handleResumeOrStartNewAttempt({
        appState,
        appStateRef,
        setAppState,
        recommendation,
        recommendationAttempt: null,
        baseState,
        history,
        ref: RoutePath.REMEDIATION_STUDENT_HOME
      }).catch(() => setIsLaunching(false));
    };
  };

  const renderRecs = (rec: RecommendationDto, isSecondRecommendation: boolean) => {
    if (rec.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
      return (
        <RecommendationCard
          banner={getBannerForRecommendation(rec, isSecondRecommendation)}
          handleLaunch={createLaunchHandler(rec)}
          recommendation={rec}
          isExpanded
          recommendationAttempts={baseState?.recommendationAttempts}
          launchButtonText="Launch"
        />
      );
    }

    const quizAssessmentGoal = getAssessmentGoalFromRecommendation(baseState?.eolsAssessment, rec);
    const statusCounts = getQuizTotals(baseState);

    return (
      <RemediationQuiz
        assessmentGoal={quizAssessmentGoal}
        recommendation={rec}
        metricCounts={statusCounts}
        showReviewQuizButton={false}
        continueQuizzing
        showBorder
      />
    );
  }

  const secondRecommendation = getSecondRecommendation(baseState, nextRecommendation);
  const nextRecs = [nextRecommendation]
  if (secondRecommendation) {
    nextRecs.push(secondRecommendation);
  }

  return (
    <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_2X, FlexLayoutModifier.MIDDLE]}>
      {nextRecs.map((nextRec, index) => {
        return (
          <React.Fragment key={nextRec.id}>
            {index > 0 && (
              <FlexItem modifiers={[FlexLayoutModifier.MIDDLE]}>
                <span className="u-els-font-size-body-large">or</span>
              </FlexItem>
            )}
            <FlexItem
              classes={['u-els-width-1o2']}
              modifiers={[FlexLayoutModifier.GROW]}>
              {renderRecs(nextRec, index === 1)}
            </FlexItem>
          </React.Fragment>
        )
      })}
    </FlexLayout>
  )
};

const RecommendationPostPlayerNext: React.FC<RecommendationPostPlayerNextPropsOnly> = RecommendationPostPlayerNextComponent;

export default RecommendationPostPlayerNext;
